import React, { useEffect, useState } from 'react';

import { statusesAll } from 'settings';

import { useTheme, Button, Stack, Typography, Box, Divider, Checkbox, Switch } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { SmartphoneNfc, Search, ScanSearch, Check } from 'lucide-react';
import { format } from 'date-fns';
import { DrawerPopup } from '../../UI/DrawerPopup';
import { FieldLabel } from '../../UI/FieldLabel';
import { Input } from '../../UI/Input';
import { fieldSurfaceSx, FIELD_HEIGHT } from '../../UI/fieldMetrics';
import { menuRowToneSx, MENU_ROW_LABEL_SX } from '../../UI/menuRowTone';
import { FilterSheetFooter } from '../../UI/FilterSheetFooter';
import { certificateSubLabel } from 'utils/certificateUtils';
import CertificatesChipList from './CertificatesChipList';
import CertificatesDateRangeFilter from './CertificatesDateRangeFilter';
import CertificatesSearchPicker from './CertificatesSearchPicker';
import useCertificatesFilterState from './useCertificatesFilterState';
import { useAutoApply } from 'hooks/useAutoApply';
import {
  CertificatesFilterTypes,
  FilteredArrayTypes,
  filtersType,
  checkedItemsType,
} from 'types/CertificatesFilter.types';
import { Calendar as ShadcnCalendar } from '@repo/shadcn-ui';
import { type DateRange } from 'react-day-picker';
import { StatusPillFilter } from 'components/pages/certificates/StatusPillFilter';
import { useNfcScan, resolveVerifiedTag } from 'hooks/useNfcScan';
import {
  OWNERSHIP_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
  type OwnershipFilterValue,
} from 'constants/certificateConstants';
import { border, font } from 'settings/tokens';

export const CertificatesFilter = ({
  certificates,
  isFilterChanged,
  filteredItems,
  onFilterSubmit,
  mobile,
  onCertificateFilterChange,
  initialSelectedCertificateIds = [],
  initialActivityFrom = null,
  initialActivityTo = null,
  statuses = [],
  onStatusesChange,
  ownership = 'current',
  onOwnershipChange,
}: CertificatesFilterTypes) => {
  /*
   * The pills' own copy of the status selection, still local — but now because a
   * control must render from something it can update on tap, NOT because it is
   * waiting behind an Apply.
   *
   * The original defect was that pills wrote straight through to the query while
   * an Apply button sat below them appearing to govern them: one sheet, two commit
   * models. Staging them fixed the contradiction from one side; removing Apply
   * fixes it from the other, and is the direction the platform's drawer settled on.
   * The pills now commit on the shared 400ms debounce like every other control here.
   */
  const [localStatuses, setLocalStatuses] = useState<string[]>(statuses);

  /*
   * VRG-511 — the ownership scope's own local copy, staged and committed on the
   * same 400ms debounce as everything else in this sheet, for the same reason
   * `localStatuses` is: a control has to render from something it can update on
   * tap, and committing straight through would fire a query per tap.
   */
  const [localOwnership, setLocalOwnership] =
    useState<OwnershipFilterValue>(ownership);

  const theme = useTheme();

  const state = useCertificatesFilterState({
    certificates,
    filteredItems,
    initialSelectedCertificateIds,
    initialActivityFrom,
    initialActivityTo,
    onFilterSubmit,
    onCertificateFilterChange,
    isFilterChanged,
  });

  const {
    checkedItems,
    filteredArray,
    showCertificatePicker,
    certificateQuery,
    selectedCertificateIds,
    showAllChips,
    showNfcScan,
    showDatePicker,
    dateRange,
    selectedRange,
    setShowCertificatePicker,
    setCertificateQuery,
    setSelectedCertificateIds,
    setShowAllChips,
    setShowNfcScan,
    setShowDatePicker,
    setDateRange,
    setSelectedRange,
    handleChange,
    resetFilters,
    submitFilters,
  } = state;

  /*
   * AUTO-APPLY. There is no Apply button in this sheet, at either breakpoint.
   *
   * Every control commits itself on a 400ms debounce — the same model, and the
   * same figure, as the platform's filter drawer (`AUTO_APPLY_MS`). The debounce
   * is not a nicety: without it, ticking three status pills is three queries, and
   * a half-drawn date range fires one for a filter the user has not finished
   * expressing. See `useAutoApply` for why the schedule hangs off the INTERACTION
   * rather than off a state-watching effect (short version: `localStatuses` is
   * synced FROM the prop this commit writes, so a state watcher would commit its
   * own echo, forever, at 400ms intervals).
   *
   * The commit body is the one the two footers used to share verbatim.
   */
  const { schedule } = useAutoApply(() => {
    submitFilters();
    onStatusesChange?.(localStatuses);
    onOwnershipChange?.(localOwnership);
  });

  /**
   * Stage a certificate selection, then commit.
   *
   * Wrapped rather than calling `schedule()` at each of the nine `setSelectedCertificateIds`
   * call sites (two NFC paths, two pickers, two chip lists, two Clears, one Reset):
   * a new one added later gets auto-apply by construction, and forgetting it at ONE
   * site would leave a control that silently does not apply — the failure mode that
   * is hardest to notice, because the sheet looks right.
   */
  const stageCertificateIds = (value: React.SetStateAction<string[]>) => {
    setSelectedCertificateIds(value);
    schedule();
  };

  /**
   * Stage the date axis, then commit — UNLESS the range is half-expressed.
   *
   * `from` without `to` is the one genuinely invalid intermediate state in this
   * sheet, and it is reachable: the mobile calendar is `mode='range'`, so the
   * first tap of a two-tap gesture always produces it. Committing there would
   * emit no date at all (`submitFilters` only serialises a range when BOTH ends
   * are set), so the query fired would be a redundant copy of the current one —
   * and, worse, if a range was already applied it would appear to clear itself
   * mid-edit and then come back 400ms after the second tap.
   *
   * So a half range stages without scheduling: the list keeps showing what is
   * applied until the range is complete. Presets and Clear are complete states by
   * construction and commit at once.
   */
  const stageDate = (
    range: 'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null,
    dates?: DateRange,
  ) => {
    setSelectedRange(range);
    setDateRange(dates);
    if (dates?.from && !dates?.to) return;
    schedule();
  };

  const nfc = useNfcScan();
  // Set when a tag reads cleanly but matches nothing the customer owns — a
  // materially different outcome from a failed read, and the one the drawer has
  // to explain rather than just saying "try again".
  const [nfcUnmatchedTag, setNfcUnmatchedTag] = useState<string | null>(null);

  /**
   * Tag -> certificate, matched against the CHIP UID the certificate carries. A local match
   * against the loaded list — no lookup endpoint needed.
   *
   * VRG-286: `tag_uid` is the chip UID; `tag_id` deliberately is NOT in this list any more
   * because it is now the certificate's uuid FK to our own `tags` row, and a scanned UID can
   * never equal it. Leaving it in would be a comparison that silently always fails.
   *
   * VRG-231 dropped the `e_tag_id` fallback that used to sit beside `tag_uid` here — it was an
   * alias carrying the identical value. `tag_uid` itself STAYS, and this is one of the two
   * reasons it is still emitted at all: this list comes from the customer's OWN certificates
   * (an authenticated projection), and matching a chip the customer is physically holding is
   * only possible if the row carries the UID. The anonymous certificate payload does not.
   */
  const findCertificateByTag = (tagUid: string) => {
    const needle = tagUid.toLowerCase();
    return certificates?.find(
      (c: any) => c?.tag_uid && String(c.tag_uid).toLowerCase() === needle,
    );
  };

  /**
   * The button starts the phone's own NFC scan directly — no "ready to scan?"
   * step, because on the one platform that can actually scan (Chrome/Android)
   * the browser shows its own native prompt, and a second confirm before it was
   * pure ceremony.
   *
   * The drawer is now only for the cases that need words: a browser that cannot
   * scan at all (every iPhone — Safari ships no Web NFC), a read that failed, or
   * a tag that isn't one of yours.
   */
  const handleNfcScan = async () => {
    setNfcUnmatchedTag(null);

    /*
     * No `isSupported` branch — the TRIGGER is gated on it now, so this cannot
     * be reached on a browser that cannot scan. It used to fall through to a
     * QR-handoff modal on iOS and desktop; see `nfc.isSupported` at the call
     * sites for why that surface is gone rather than hidden.
     */
    const tag = await nfc.scan();
    if (!tag) {
      // Cancelled, or failed — `nfc.error` distinguishes them for the drawer.
      if (nfc.error) setShowNfcScan(true);
      return;
    }

    // Verify first, exactly as an iOS tap does. Reading a tag over Web NFC only
    // proves some tag was present; the proof is what makes the scan genuine, and
    // it is checked server-side. Doing this means Android and iOS get the same
    // guarantee rather than Android silently getting a weaker one.
    const verified = await resolveVerifiedTag(tag.url);
    if (verified) {
      stageCertificateIds((prev: string[]) =>
        prev.includes(verified.certificateId) ? prev : [...prev, verified.certificateId],
      );
      setShowNfcScan(false);
      return;
    }

    // Not proven — either the proof failed or the round-trip couldn't be made.
    // Fall back to matching the id against what's loaded, which still answers
    // "which of my certificates is this?" without claiming the tag is authentic.
    const match = findCertificateByTag(tag.tagId);
    if (match) {
      stageCertificateIds((prev: string[]) =>
        prev.includes(match.id) ? prev : [...prev, match.id],
      );
      setShowNfcScan(false);
      return;
    }

    setNfcUnmatchedTag(tag.tagId);
    setShowNfcScan(true);
  };

  useEffect(() => {
    if (!selectedRange && initialActivityFrom && initialActivityTo) {
      setDateRange({ from: new Date(initialActivityFrom), to: new Date(initialActivityTo) });
    }
  }, []);

  useEffect(() => {
    setLocalStatuses(statuses);
  }, [statuses]);

  /*
   * ONE reset for both branches.
   *
   * The desktop footer is a second hand-roll of the mobile one, and that is
   * precisely why this bug shipped twice (§36): the mobile copy cleared the
   * STAGED pills (`setLocalStatuses([])`), the desktop copy called the page's
   * committed setter `onStatusesChange?.([])` instead — so desktop Reset wrote
   * through to the query immediately AND left the pills looking selected,
   * because nothing cleared the staged array it renders from.
   *
   * Under auto-apply, Reset commits — clearing IS the change, and there is no
   * Apply left for it to wait behind. It goes through the SAME `schedule()` as
   * every other control rather than calling the commit inline, for a mechanical
   * reason: React has not flushed the `setState`s above by the time this handler
   * returns, so an inline commit would serialise the PREVIOUS filters and clear
   * nothing. It still does not close the sheet — you stay where you are, which
   * is the point of clearing a filter in order to re-pick one.
   */
  const handleReset = () => {
    resetFilters();
    setLocalStatuses([]);
    // VRG-511 — back to the list's default meaning. This is the only axis in the
    // sheet whose non-default value HIDES rows the user owns, so leaving it behind
    // a Reset would make "clear filters" the one action that cannot restore the
    // list to what it looked like before.
    setLocalOwnership('current');
    schedule();
  };

  /*
   * VRG-511 — SINGLE-select, enforced here rather than in the component.
   *
   * `StatusPillFilter` is a multi-select control and stays one; these three pills
   * are mutually exclusive SCOPES, not a set, so re-tapping the active pill is a
   * no-op rather than a deselect. Deselecting would leave no scope at all, which
   * has no meaning — "show me certificates that are neither mine nor formerly
   * mine" is not a question. Reusing the component with a different handler was
   * the cheaper correct move than a second pill component (§28/§47).
   */
  const handleOwnershipToggle = (value: string) => {
    const next = value as OwnershipFilterValue;
    if (next === localOwnership) return;
    setLocalOwnership(next);
    schedule();
  };

  // VERA-315: 'all' clears the specific-status selection (statuses never stores 'all'
  // itself); any specific status toggles in/out of the multi-select array.
  const handleStatusToggle = (value: string) => {
    if (value === 'all') {
      setLocalStatuses([]);
      schedule();
      return;
    }
    setLocalStatuses(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value],
    );
    schedule();
  };

  /*
   * The applied-filter COUNT is gone from this file, and that is a consequence of
   * auto-apply rather than a tidy-up.
   *
   * It was `applyCount`, and before that two copies of the same sum (mobile inline,
   * desktop rebuilt inside an IIFE as `countToApply`) which differed by one term
   * that could never be non-zero — the copies agreed numerically while disagreeing
   * in source, which is the state in which the next edit to one silently breaks the
   * other. Both fed exactly one thing: the `Apply (n)` label. With no Apply button
   * there is nothing left for a count to label here; the toolbar's Filter badge is
   * where "how many filters are on" belongs, and it reads the APPLIED filters
   * rather than a staged set that no longer exists.
   */

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/*
        * Desktop pays its own 16px side padding; mobile keeps 0 and takes DrawerPopup's.
        *
        * DrawerPopup pads its content `px={anchor === 'bottom' ? 2 : 0}` — 16px on a bottom
        * sheet, NOTHING on a side panel. So on desktop every control in here sat flush against
        * the panel's edge (measured: the Certificate label's left == the Paper's left, to the
        * pixel) while the panel's own header and footer were both inset 16px.
        *
        * 16 and not the platform drawer's 24: DrawerPopup hard-codes `p: '16px'` on the header
        * it draws for EVERY drawer in this app and exposes no prop for it, so a 24px body
        * leaves the "Filters" title and the first field label on two different left edges —
        * measured as an 8px stagger, which is more visible than the 8px this panel is narrower
        * than platform's. Matching the header is the version of "consistent" available here.
        */}
      {/*
        * THE SCROLL REGION, on DESKTOP only.
        *
        * This was a plain `Box` in normal flow, which is why the desktop panel's footer
        * floated: with nothing claiming the free space, `mt: 'auto'` on the footer had
        * none to claim (see `FILTER_PANEL_CONTENT_SX`). Making the body the flex item
        * that grows pins the footer under it AND gives the certificate list somewhere
        * to scroll instead of pushing the buttons off the panel.
        *
        * `minHeight: 0` is the non-obvious half — a flex item defaults to
        * `min-height: auto`, sized to its CONTENT, so without it the body refuses to
        * shrink to its `flex` share and `overflowY: auto` never engages.
        *
        * NOT on mobile, and that is measured rather than assumed. The bottom sheet is
        * content-height and scrolls at the PAPER level (`maxHeight: 90vh`, and MUI's
        * `.MuiDrawer-paper` is `overflowY: auto` — which is exactly why DrawerPopup's
        * header is `position: sticky`). Adding a second scroll container inside it made
        * the body a 297px box around 301px of content: 4px of overflow, enough to draw a
        * scrollbar thumb inside a sheet that had nothing to scroll. One scroll owner per
        * surface; on a phone that owner is the Paper.
        */}
      <Box
        px={mobile ? 0 : 2}
        mb={2}
        sx={mobile ? undefined : { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
      >
        {mobile ? (
          <>
            <FieldLabel label='Certificates' />
            <CertificatesSearchPicker
              mobile
              open={showCertificatePicker}
              onOpen={() => setShowCertificatePicker(true)}
              onClose={() => setShowCertificatePicker(false)}
              query={certificateQuery}
              onQueryChange={setCertificateQuery}
              certificates={certificates}
              selectedIds={selectedCertificateIds}
              onToggleId={(id, checked) =>
                stageCertificateIds(prev =>
                  checked ? [...prev, id] : prev.filter(x => x !== id)
                )
              }
              onClear={() => stageCertificateIds([])}
              onOpenNfc={nfc.isSupported ? handleNfcScan : undefined}
            />
            {selectedCertificateIds.length > 0 && (
              <CertificatesChipList
                stacked
                certificates={certificates}
                ids={selectedCertificateIds}
                label={'Selected certificates'}
                showAll={showAllChips}
                onToggleShowAll={() => setShowAllChips(!showAllChips)}
                onRemove={id => stageCertificateIds(prev => prev.filter(cid => cid !== id))}
              />
            )}
            <Box sx={{ borderTop: `1px solid ${'gray.300'}`, my: 2 }} />
            <CertificatesDateRangeFilter
              mobile
              selectedRange={selectedRange}
              selectedDateFrom={dateRange?.from ?? null}
              selectedDateTo={dateRange?.to ?? null}
              open={showDatePicker}
              onOpen={() => setShowDatePicker(true)}
              onClose={() => setShowDatePicker(false)}
              onClear={() => stageDate(null, undefined)}
              onSelectRange={key => stageDate(key as any, undefined)}
              onSelectDate={() => stageDate(null, undefined)}
            />
            <div className='flex flex-col gap-3 mt-4'>
              <FieldLabel
                label='Status'
              />
              <StatusPillFilter
                options={STATUS_FILTER_OPTIONS}
                selected={localStatuses.length === 0 ? ['all'] : localStatuses}
                onToggle={handleStatusToggle}
              />
            </div>
            <div className='flex flex-col gap-3 mt-4'>
              {/* VRG-511 — ownership scope. See the mobile branch; the two are
                  hand-duplicated in this file already, so this section is too. */}
              <FieldLabel
                label='Ownership'
              />
              <StatusPillFilter
                options={OWNERSHIP_FILTER_OPTIONS}
                selected={[localOwnership]}
                onToggle={handleOwnershipToggle}
              />
            </div>
          </>
        ) : (
          <>
            <FieldLabel label='Certificate' />
            <Box sx={{ position: 'relative' }}>
              <CertificatesSearchPicker
                open={showCertificatePicker}
                onOpen={() => setShowCertificatePicker(true)}
                onClose={() => setShowCertificatePicker(false)}
                query={certificateQuery}
                onQueryChange={q => {
                  setCertificateQuery(q);
                }}
                certificates={certificates}
                selectedIds={selectedCertificateIds}
                onToggleId={(id, checked) =>
                  stageCertificateIds(prev =>
                    checked ? [...prev, id] : prev.filter(x => x !== id)
                  )
                }
                onClear={() => stageCertificateIds([])}
                onOpenNfc={nfc.isSupported ? handleNfcScan : undefined}
              />
            </Box>
            {!showCertificatePicker && selectedCertificateIds.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <CertificatesChipList
                  certificates={certificates}
                  ids={selectedCertificateIds}
                  showImages
                  label={'Selected certificates'}
                  limit={6}
                  showAll={showAllChips}
                  onToggleShowAll={() => setShowAllChips(!showAllChips)}
                  onRemove={id => stageCertificateIds(prev => prev.filter(cid => cid !== id))}
                  chipTitleFontSize={13}
                  chipPadding={{ px: 1, py: 0.5 }}
                />
              </Box>
            )}
            <Box sx={{ borderTop: `1px solid ${'gray.300'}`, my: 2 }} />
            <CertificatesDateRangeFilter
              selectedRange={selectedRange}
              selectedDateFrom={dateRange?.from ?? null}
              selectedDateTo={dateRange?.to ?? null}
              open={showDatePicker}
              onOpen={() => setShowDatePicker(true)}
              onClose={() => setShowDatePicker(false)}
              onClear={() => stageDate(null, undefined)}
              onSelectRange={key => stageDate(key as any, undefined)}
              onSelectDate={() => stageDate(null, undefined)}
            />
            <Box sx={{ borderTop: `1px solid ${'gray.300'}`, my: 2 }} />
            <FieldLabel
              label='Status'
            />
            <StatusPillFilter
              options={STATUS_FILTER_OPTIONS}
              selected={localStatuses.length === 0 ? ['all'] : localStatuses}
              onToggle={handleStatusToggle}
            />
            {/* VRG-511 — ownership scope, LAST on mobile: it is the rarest axis
                (most people never leave `Owned`) and vertical space on a phone is
                the scarce thing. The two breakpoint branches in this file are
                hand-duplicated already, so this section is duplicated too rather
                than extracted — matching the file's existing shape beats
                introducing a third structure for one section. */}
            <Box sx={{ borderTop: `1px solid ${'gray.300'}`, my: 2 }} />
            <FieldLabel
              label='Ownership'
            />
            <StatusPillFilter
              options={OWNERSHIP_FILTER_OPTIONS}
              selected={[localOwnership]}
              onToggle={handleOwnershipToggle}
            />
          </>
        )}
      </Box>
      {mobile && (
        <DrawerPopup
          title='Certificate'
          anchor={'bottom'}
          rightActionLabel='Done'
          rightActionOnClick={() => {
            setShowCertificatePicker(false);
          }}
          paperSx={{ height: 'auto', maxHeight: '85vh' }}
          handleOpen={showCertificatePicker}
          handleClose={() => setShowCertificatePicker(false)}
          child={
            <Stack spacing={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Stack direction={'row'} spacing={1} alignItems={'center'}>
                {/* Was 44 tall where every other field-shaped control is `FIELD_HEIGHT`,
                    took the tenant's radius UNCLAMPED (`theme.shape.borderRadius`, which
                    is the whole reason `getFieldRadius` exists), and hardcoded `#374151`
                    for its text. The base field carries all three. */}
                <Input
                  value={certificateQuery}
                  onChange={setCertificateQuery}
                  placeholder='Search'
                  startAdornment={<Search size={16} />}
                  inputProps={{ 'aria-label': 'Search certificates' }}
                />
                {/* Chrome/Android only. `NDEFReader` does not exist on iOS Safari
                    or on any desktop browser, so there the control is absent —
                    not disabled, and no longer a QR-handoff modal. See
                    `handleNfcScan`. */}
                {nfc.isSupported && (
                  <Button
                    onClick={handleNfcScan}
                    variant='text'
                    // Square to the field it sits beside, on the shared surface. It was
                    // 44 with a hardcoded 12px radius while the field beside it was 44
                    // with the tenant's radius, so the pair matched on neither.
                    sx={theme => ({
                      ...fieldSurfaceSx(theme),
                      width: FIELD_HEIGHT,
                      height: FIELD_HEIGHT,
                      minWidth: 'auto',
                      p: 0,
                      '&:hover': {
                        backgroundColor: border.subtle,
                      },
                    })}
                  >
                    <SmartphoneNfc size={16} />
                  </Button>
                )}
              </Stack>

              {selectedCertificateIds.length > 0 && (
                <Stack spacing={1}>
                  <Typography sx={{ color: '#1f2937' }}>
                    Selected items: {selectedCertificateIds.length}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {(showAllChips
                      ? selectedCertificateIds
                      : selectedCertificateIds.slice(0, 6)
                    ).map(id => {
                      const cert = certificates.find(c => c.id === id);
                      return (
                        <Box
                          key={id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: theme => theme.palette.grey[50],
                            borderRadius: '12px',
                            px: 1.5,
                            py: 0.75,
                          }}
                        >
                          <Typography sx={{ fontSize: 14 }}>
                            {cert?.product?.title ?? cert?.name ?? 'Unnamed certificate'}
                          </Typography>
                          <Box
                            sx={{ cursor: 'pointer', fontSize: 16, fontWeight: 'bold' }}
                            onClick={() =>
                              stageCertificateIds(
                                selectedCertificateIds.filter(cid => cid !== id)
                              )
                            }
                          >
                            ×
                          </Box>
                        </Box>
                      );
                    })}
                    {selectedCertificateIds.length > 6 && !showAllChips && (
                      <Box
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: theme.palette.primary.main,
                          fontSize: 14,
                          alignSelf: 'center',
                          px: 1,
                        }}
                        onClick={() => setShowAllChips(true)}
                      >
                        Show all
                      </Box>
                    )}
                    {selectedCertificateIds.length > 6 && showAllChips && (
                      <Box
                        sx={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          color: theme.palette.primary.main,
                          fontSize: 14,
                          alignSelf: 'center',
                          px: 1,
                        }}
                        onClick={() => setShowAllChips(false)}
                      >
                        Show less
                      </Box>
                    )}
                  </Box>
                </Stack>
              )}

              <Stack spacing={1.25} sx={{ flex: 1, overflow: 'auto' }}>
                {certificates
                  .filter(c =>
                    ((c.product?.title ?? c.name ?? '') as string)
                      .toLowerCase()
                      .includes(certificateQuery.toLowerCase())
                  )
                  .map(cert => (
                    <Stack key={cert.id} direction={'row'} alignItems={'center'} spacing={1.25}>
                      <Checkbox
                        size='small'
                        checked={selectedCertificateIds.includes(cert.id)}
                        onChange={e => {
                          stageCertificateIds(prev =>
                            e.target.checked
                              ? [...prev, cert.id]
                              : prev.filter(id => id !== cert.id)
                          );
                        }}
                        icon={
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              border: '1px solid',
                              borderColor: '#6b7280',
                              borderRadius: '6px',
                            }}
                          />
                        }
                        checkedIcon={
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '6px',
                              backgroundColor: '#000000',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Box
                              component='span'
                              sx={{
                                width: 12,
                                height: 7,
                                borderBottom: '2px solid #ffffff',
                                borderLeft: '2px solid #ffffff',
                                transform: 'rotate(-45deg)',
                                mt: '-1px',
                              }}
                            />
                          </Box>
                        }
                        sx={{
                          p: 0,
                          color: '#1f2937',
                          '& .MuiSvgIcon-root': { fontSize: 20 },
                          '&.Mui-checked': { color: '#000000' },
                          '& .MuiTouchRipple-root': { display: 'none' },
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      />
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          backgroundColor: 'gray.200',
                          border: '1px solid',
                          borderColor: border.subtle,
                          overflow: 'hidden',
                          flex: '0 0 36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {cert.product?.images?.[0] ? (
                          <img
                            src={cert.product.images[0]}
                            alt='certificate'
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 40 40'
                            fill='none'
                          >
                            <path
                              fillRule='evenodd'
                              clipRule='evenodd'
                              d='M33 10H7V30H33V10ZM8.01961 29V11H31.9804V29H8.01961ZM14.6471 16C14.6471 17.1046 13.7341 18 12.6078 18C11.4816 18 10.5686 17.1046 10.5686 16C10.5686 14.8954 11.4816 14 12.6078 14C13.7341 14 14.6471 14.8954 14.6471 16ZM10.0588 27V24.5L14.6471 20L16.6863 22L23.8235 15L29.9412 21V27H10.0588Z'
                              fill='#8B8D8F'
                              opacity={0.5}
                            />
                          </svg>
                        )}
                      </Box>
                      <Stack spacing={0}>
                        <Typography sx={{ fontSize: 14 }}>
                          {cert.product?.title ?? cert.name ?? 'Unnamed certificate'}
                        </Typography>
                        {/* VRG-231: was `sku ?? tag_uid ?? e_tag_id`. The chip UID is never
                            shown to a customer, and dropping the fallback outright would make
                            two identical items render identical rows — so it falls back to the
                            certificate's own short id, which is also what support asks for. */}
                        <Typography sx={{ fontSize: font.label, color: 'text.secondary' }}>
                          {certificateSubLabel(cert)}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
              </Stack>

              <Box
                sx={{
                  borderTop: `1px solid ${'gray.300'}`,
                  pt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant='text'
                  onClick={() => stageCertificateIds([])}
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: border.subtle,
                    width: '100%',
                    px: 3,
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Stack>
          }
        />
      )}
      {mobile && (
        <DrawerPopup
          title='Activity'
          anchor={'bottom'}
          fullScreen={false}
          rightActionLabel='Done'
          rightActionOnClick={() => setShowDatePicker(false)}
          paperSx={{ maxHeight: '85vh' }}
          handleOpen={showDatePicker}
          handleClose={() => setShowDatePicker(false)}
          child={
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <Stack spacing={0.5} sx={{ px: 1, pt: 1 }}>
                  {[
                    { key: 'last_month', label: 'Last month' },
                    { key: 'last_3m', label: 'Last 3 months' },
                    { key: 'last_6m', label: 'Last 6 months' },
                    { key: 'last_12m', label: 'Last 12 months' },
                  ].map(opt => {
                    const selected = selectedRange === opt.key;
                    return (
                      <Stack
                        key={opt.key}
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        onClick={() => {
                          // toggle: tapping the selected range clears it
                          stageDate(selected ? null : (opt.key as any), undefined);
                        }}
                        /*
                         * The shared menu-row tone (`components/UI/menuRowTone`):
                         * `text.secondary` at rest, `text.primary` when selected,
                         * and the brand accent + a grey wash on hover/focus.
                         *
                         * The SELECTED row keeps its own resting wash and darkens
                         * a step further on hover, so "chosen" and "pointing at"
                         * never collapse into the same pixel — spreading the tone
                         * alone would send a hovered selected row to the same
                         * `grey[50]` an unselected one gets, i.e. LIGHTER than it
                         * sits at rest. The hover text colour still comes from the
                         * shared block, so only the background is overridden.
                         */
                        sx={theme => {
                          const tone = menuRowToneSx(theme, { selected });
                          return {
                            cursor: 'pointer',
                            px: 2,
                            py: 2,
                            minHeight: 52,
                            borderRadius: '12px',
                            backgroundColor: 'transparent',
                            ...tone,
                            ...(selected && {
                              backgroundColor: theme.palette.grey[100],
                              '&:hover, &:focus-visible': {
                                ...tone['&:hover, &:focus-visible'],
                                backgroundColor: theme.palette.grey[200],
                              },
                            }),
                          };
                        }}
                      >
                        {/* Weight is deliberately NOT stepped for selection — at
                            16px a 600 lays down noticeably more ink, and colour
                            alone is the lighter instrument (see menuRowTone). */}
                        <Typography sx={{ fontSize: 16, fontWeight: 400, ...MENU_ROW_LABEL_SX }}>
                          {opt.label}
                        </Typography>
                        {/* `currentColor` so the tick travels with the row —
                            hardcoded #000000 left a black check beside an
                            accent-coloured label on hover. */}
                        {selected && <Check size={20} color='currentColor' strokeWidth={2} />}
                      </Stack>
                    );
                  })}
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    px: 1,
                    pb: 2,
                    overflowX: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <ShadcnCalendar
                      mode='range'
                      className='rounded-md [--cell-size:2.75rem] md:[--cell-size:3rem]'
                      selected={dateRange ?? undefined}
                      /* `mode='range'`, so the FIRST tap of a two-tap gesture
                         hands back `{from, to: undefined}`. `stageDate` stages
                         that without committing — see its note on why a half
                         range must not fire a query. */
                      onSelect={date => stageDate(null, date)}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>
              <Button
                fullWidth
                variant='text'
                onClick={() => stageDate(null, undefined)}
                sx={{
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: border.subtle,
                  height: 40,
                }}
              >
                Reset
              </Button>
            </Box>
          }
        />
      )}
      {mobile && (
        <DrawerPopup
          title='Filter by scan'
          anchor={'bottom'}
          rightActionLabel='Cancel'
          rightActionOnClick={() => setShowNfcScan(false)}
          paperSx={{ height: 'auto', maxHeight: '85vh' }}
          handleOpen={showNfcScan}
          handleClose={() => setShowNfcScan(false)}
          child={
            // Only the outcomes that need words. The happy path never opens this
            // drawer at all — the button starts the phone's own scan and, on a
            // match, selects the certificate and closes.
            <Box sx={{ display: 'flex', flexDirection: 'column', pb: 3 }}>
              <Stack spacing={2} alignItems='center' sx={{ px: 2, py: 3 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: theme => theme.palette.grey[50],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ScanSearch size={24} />
                </Box>

                {/* The "this browser cannot scan" branch is GONE, not hidden.
                    The trigger is now gated on `nfc.isSupported`, so a browser
                    without Web NFC never renders a scan control and can never
                    open this drawer — the branch was unreachable the moment that
                    gate went in, and an unreachable explanation is worse than
                    none because it reads as a state the app can still be in. */}
                {nfcUnmatchedTag ? (
                  <>
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                      No certificate for that tag
                    </Typography>
                    <Typography
                      sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}
                    >
                      That tag was read, but none of your certificates use it.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                      Couldn't read that tag
                    </Typography>
                    <Typography
                      sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 280, lineHeight: 1.5 }}
                    >
                      {nfc.error || 'Try holding the tag against the back of your phone again.'}
                    </Typography>
                  </>
                )}
              </Stack>
            </Box>
          }
        />
      )}
      {/*
        THE DESKTOP QR MODAL IS DELETED, not gated.

        It offered "Scan with your smartphone" over a QR code — a handoff to a
        phone that would then do the scanning. Two things were wrong with it, and
        the second is why gating it was not enough:

          1. The QR was HARDCODED to `https://scan.vera.com/session/12345`. Not a
             template, not a session id — a literal placeholder. Nothing has ever
             resolved it, so the code could not be scanned into anything. Both
             footer buttons (`Cancel` and `Apply`) also just called `onClose`.
          2. Even working, the handoff only pays off where the phone can scan,
             which is Chrome/Android — and an Android user does not need a QR to
             reach their own phone. On iOS the receiving phone still cannot start
             a Web NFC scan, so the handoff would end where it began.

        Desktop and iOS now render no scan affordance at all. Tapping the tag is
        the path on iOS (the OS opens the certificate itself); on desktop there is
        no reader, and search by certificate covers the need.
      */}
      <Box
        sx={{
          mt: 'auto',
          /*
           * PINNED TO THE BOTTOM OF THE SHEET ON MOBILE.
           *
           * `mt: 'auto'` alone only pushes the footer to the end of the flex
           * column — which is the bottom of the CONTENT, not of the viewport. The
           * mobile sheet is content-height up to `maxHeight`, and once the filter
           * list outgrows that the body scrolls and Reset scrolls away with it.
           * On a phone that is the one control you reach for after scrolling
           * through the statuses and the calendar, so it was the first thing to
           * leave the screen.
           *
           * Sticky rather than fixed: fixed would escape the sheet and sit over
           * the page behind it. Sticky keeps it in the sheet's own flow, so it
           * still sits after the content when there is little of it and pins only
           * once there is more than fits.
           *
           * The BACKGROUND is not optional — a transparent sticky footer lets the
           * filter rows scroll visibly underneath the button. `background.paper`
           * is the sheet's own fill, so it reads as the sheet's floor rather than
           * as a panel laid on top.
           *
           * Desktop is untouched: that panel is a real flex column whose body
           * scrolls independently, so its footer is already pinned by layout (see
           * the note on the desktop `FilterSheetFooter` below).
           */
          ...(mobile
            ? {
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                backgroundColor: theme => theme.palette.background.paper,
              }
            : {}),
        }}
      >
        {mobile ? (
          /*
            RESET ONLY — no Apply, at either breakpoint (see the auto-apply note
            at the top of this component). That is `FilterSheetFooter`'s
            documented second mode rather than an omission: omitting `onApply`
            renders the reset-only footer, which is what the platform's filter
            drawer has always used.

            `count` goes with it. It only ever existed to label the Apply button
            (`Apply (3)`), so with no button there is nothing for it to label —
            the toolbar's own Filter badge is where the applied count belongs.
          */
          <FilterSheetFooter onReset={handleReset} />
        ) : (
          <>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ borderTop: `1px solid ${'gray.300'}`, px: 2, py: 1 }}
            >
              <Typography sx={{ color: 'text.secondary' }}>
                {filteredArray.length > 0 ? `Found ${filteredArray.length} items` : ''}
              </Typography>
            </Stack>
            {/*
              * THE SHARED FOOTER, on desktop too — RESET ONLY (VERA-315).
              *
              * This was the last hand-rolled filter footer in either app: a MUI
              * `outlined` Reset beside a `@repo/ui` primary Apply, holding its own
              * copies of the Reset-does-not-apply and the not-disabled-at-zero rules
              * in comments. Both rules now live once, in the component
              * (`@repo/ui` `FilterSheetFooter`), which the platform's filter drawer
              * also renders — the parity the user asked for is that the two apps run
              * the same code, not that someone remembers to mirror an edit.
              *
              * NO `onApply`, which is what selects the shared component's auto-apply
              * mode. Note that this is NOT the same as deleting the footer: `main`
              * has a hand-rolled Reset + Apply pair here, and dropping the whole
              * block to "make desktop auto" would take the Reset with it. Reset is a
              * separate, requested control and it is the only way back to an
              * unfiltered list from inside the panel — which matters MORE under
              * auto-apply, not less, because there is no longer an Apply that can
              * commit a cleared set.
              *
              * `mode='panel'`: right-aligned, intrinsic-width, 40px controls — a
              * pointer-driven side panel, not a thumb-driven phone sheet. Same
              * geometry the platform drawer's footer uses, and right-aligned for the
              * same reason its lone `Clear` is: this panel is 550px, so alignment
              * has something to align against (`fullWidthReset` is for the ~296px
              * Layout popover, where it does not).
              *
              * DROPPED with the hand-roll:
              *  - `position: sticky; bottom: 16`. The panel is now a real flex column
              *    with a scrolling body above this, so the footer is pinned by layout.
              *    Sticky was compensating for a body that did not scroll, and it never
              *    worked anyway — nothing was taller than its scroll container.
              *  - `glassSx`. Its whole purpose was to let the filter list read through
              *    a bar it scrolled UNDER. Nothing passes under this footer any more.
              *
              * 16px sides match the body above and DrawerPopup's own header inset. NO
              * bottom padding, deliberately: DrawerPopup's content Box already pays
              * `py: 3` (24px) below this, and adding one double-counts.
              */}
            {/*
              `fullWidthReset` — Reset spans the footer rather than sitting as a
              120px button at its right end.

              This diverges from `FilterSheetFooter`'s own stated axis, which is
              the CONTAINER'S WIDTH: right-align in a wide panel (the platform's
              500px filter drawer), stretch only in a narrow one (this app's
              ~296px Layout popover). By that rule this 550px drawer is wide and
              should right-align, which is what it did.

              The rule was written for a footer holding a PAIR — "a pair of
              controls sitting at the end of a wide row" — and under auto-apply
              there is no pair here any more: Apply is gone and Reset is alone.
              A lone 120px button at the far end of a 518px row is not reading as
              alignment, it is reading as a button that lost its partner. User
              call, asked twice.
            */}
            <FilterSheetFooter
              mode='panel'
              fullWidthReset
              onReset={handleReset}
              sx={{ marginTop: 0, paddingTop: '8px', paddingLeft: '16px', paddingRight: '16px' }}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
